import { AttachFileRounded, AutoFixHighOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../components/UIToolkit/Badges/MetaChip";
import { ChatMessageBubble } from "../../../components/UIToolkit/Chat/ChatMessageBubble";
import { EyebrowLabel } from "../../../components/UIToolkit/molecules/EyebrowLabel";
import { CustomPrintoutRequestBubble } from "./CustomPrintoutRequestBubble";
import { CustomPrintoutSummaryQuestion } from "./CustomPrintoutSummaryQuestion";
import { BUBBLE_MAX_WIDTH, BUBBLE_SX } from "./consts";
import type { ChatEntry } from "./customPrintoutDraftState";

type Props = {
	entry: ChatEntry;
	index: number;
	/** Annie is designing: a question of hers waits for her to finish. */
	isGenerating: boolean;
	/** The answer to Annie's "make it a summary?". */
	onAnswerSummaryQuestion: (accepted: boolean) => void;
};

/**
 * One exchange of the design chat: who asked, what they asked (with the example, if one was attached) and Annie's one-line
 * summary of what changed — or why nothing did, or her question when the request reads as a summary. The automatic
 * repair has no request of its own; it reads as Annie's note that she fixed something.
 */
export const CustomPrintoutTurn: FC<Props> = ({ entry, index, isGenerating, onAnswerSummaryQuestion }) => {
	const { t } = useTranslation();
	const request = entry.kind === "turn" ? entry.turn.request : entry.request;
	const hasAttachment = entry.kind === "turn" ? entry.turn.hasAttachment : entry.kind === "question" && entry.hasAttachment;
	const isAutomaticRepair = entry.kind === "turn" ? entry.turn.isRepair && request === "" : entry.kind === "error" && request === "";
	return (
		<Box data-testid={`custom-printout-turn-${index}`} sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
			{(request !== "" || hasAttachment) && (
				<CustomPrintoutRequestBubble author={entry.kind === "turn" ? entry.author : { kind: "viewer" }} dataTestId={`custom-printout-turn-${index}-request`}>
					{request}
					{hasAttachment && (
						<Box sx={{ mt: request === "" ? 0 : 0.5 }}>
							<MetaChip icon={<AttachFileRounded />} label={t("printout.custom.ai.turn.attachment")} tone="neutral" />
						</Box>
					)}
				</CustomPrintoutRequestBubble>
			)}
			{entry.kind === "question" ? (
				<CustomPrintoutSummaryQuestion
					summary={entry.summary}
					answer={entry.answer}
					disabled={isGenerating}
					onAnswer={onAnswerSummaryQuestion}
					dataTestId={`custom-printout-turn-${index}-summary-question`}
				/>
			) : (
				<ChatMessageBubble sender="assistant" tone={entry.kind === "error" ? "error" : "default"} maxWidth={BUBBLE_MAX_WIDTH} sx={BUBBLE_SX}>
					{isAutomaticRepair && (
						<EyebrowLabel sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25, color: "inherit" }}>
							<AutoFixHighOutlined sx={{ fontSize: "inherit" }} />
							{t(entry.kind === "turn" ? "printout.custom.ai.turn.repaired" : "printout.custom.ai.turn.repair.failed")}
						</EyebrowLabel>
					)}
					{entry.kind === "turn" ? entry.turn.summary : t(entry.errorKey)}
				</ChatMessageBubble>
			)}
		</Box>
	);
};
